/* tslint:disable:trailing-comma */
/* eslint-disable @typescript-eslint/camelcase */

import { PaletteTheme } from '@kite-js/theme-types';
import {
    alpha_black_15,
    alpha_black_60,
    alpha_black_80,
    alpha_white_20,
    alpha_white_30,
    background_100,
    background_150,
    background_20,
    background_200,
    background_300,
    background_50,
    background_500,
    background_800,
    blue_100,
    blue_200,
    blue_300,
    blue_50,
    blue_500,
    green_300,
    green_400,
    green_50,
    green_500,
    orange_300,
    orange_400,
    orange_50,
    orange_500,
    red_300,
    red_400,
    red_50,
    red_500,
    text_100,
    text_300,
    text_500,
    text_800,
    text_white,
    white,
} from './colors';
import { coreDefaultTheme } from '@kite-js/theme';

const palette: Partial<PaletteTheme> = {
    primary: {
        darkSolid: {
            background: blue_500,
            text: white,
        },
        solid: {
            background: blue_300,
            text: white,
        },
        light: {
            background: blue_100,
            text: white,
        },
        bordered: {
            background: white,
            border: blue_300,
            text: blue_300,
        },
        plainText: {
            text: blue_300,
        },
    },
    secondary: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_150,
            text: white,
        },
        light: {
            background: background_150,
            text: white,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    darkmode: {
        darkSolid: {
            background: background_800,
            text: white,
        },
        solid: {
            background: background_300,
            text: white,
        },
        light: {
            background: background_100,
            text: text_800,
        },
        bordered: {
            background: white,
            border: background_100,
            text: text_800,
        },
        plainText: {
            text: text_500,
        },
        gradient: {
            backgroundStart: background_300,
            backgroundEnd: background_800,
            text: white,
        },
    },
    price: {
        darkSolid: {
            background: red_500,
            text: white,
        },
        solid: {
            background: red_300,
            text: white,
        },
        light: {
            background: red_50,
            text: red_400,
        },
        bordered: {
            background: white,
            border: red_300,
            text: red_300,
        },
        plainText: {
            text: red_300,
        },
    },
    disable: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_150,
            text: white,
        },
        light: {
            background: background_150,
            text: white,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_500,
        },
        plainText: {
            text: text_500,
        },
        gradient: {
            backgroundStart: background_150,
            backgroundEnd: background_150,
            text: white,
        },
    },
    success: {
        darkSolid: {
            background: green_500,
            text: white,
        },
        solid: {
            background: green_300,
            text: white,
        },
        light: {
            background: green_50,
            text: green_400,
        },
        bordered: {
            background: white,
            border: green_300,
            text: green_300,
        },
        plainText: {
            text: green_300,
        },
        gradient: {
            backgroundStart: green_300,
            backgroundEnd: green_300,
            text: white,
        },
    },
    warning: {
        darkSolid: {
            background: orange_400,
            text: white,
        },
        solid: {
            background: orange_300,
            text: white,
        },
        light: {
            background: orange_50,
            text: green_400,
        },
        bordered: {
            background: white,
            border: orange_300,
            text: orange_300,
        },
        plainText: {
            text: orange_300,
        },
        gradient: {
            backgroundStart: orange_300,
            backgroundEnd: orange_300,
            text: white,
        },
    },
    error: {
        darkSolid: {
            background: red_400,
            text: white,
        },
        solid: {
            background: red_300,
            text: white,
        },
        light: {
            background: red_50,
            text: red_300,
        },
        bordered: {
            background: white,
            border: red_300,
            text: red_300,
        },
        plainText: {
            text: red_300,
        },
        gradient: {
            backgroundStart: red_300,
            backgroundEnd: red_300,
            text: white,
        },
    },
    notice: {
        darkSolid: {
            background: background_800,
            text: white,
        },
        solid: {
            background: background_800,
            text: white,
        },
        light: {
            background: blue_50,
            text: blue_500,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
        gradient: {
            backgroundStart: background_800,
            backgroundEnd: background_800,
            text: white,
        },
    },
    positive: {
        darkSolid: {
            background: green_500,
            text: white,
        },
        solid: {
            background: green_300,
            text: white,
        },
        light: {
            background: green_50,
            text: green_400,
        },
        bordered: {
            background: white,
            border: green_300,
            text: green_300,
        },
        plainText: {
            text: green_300,
        },
        gradient: {
            backgroundStart: green_300,
            backgroundEnd: green_300,
            text: white,
        },
    },
    urgency: {
        darkSolid: {
            background: orange_400,
            text: white,
        },
        solid: {
            background: orange_300,
            text: white,
        },
        light: {
            background: orange_50,
            text: orange_400,
        },
        bordered: {
            background: white,
            border: orange_300,
            text: orange_300,
        },
        plainText: {
            text: orange_300,
        },
        gradient: {
            backgroundStart: orange_300,
            backgroundEnd: orange_300,
            text: white,
        },
    },
    alert: {
        darkSolid: {
            background: red_400,
            text: white,
        },
        solid: {
            background: red_300,
            text: white,
        },
        light: {
            background: red_50,
            text: red_300,
        },
        bordered: {
            background: white,
            border: red_50,
            text: red_400,
        },
        plainText: {
            text: red_300,
        },
        gradient: {
            backgroundStart: red_300,
            backgroundEnd: red_300,
            text: white,
        },
    },
    neutral: {
        darkSolid: {
            background: background_800,
            text: white,
        },
        solid: {
            background: background_800,
            text: white,
        },
        light: {
            background: blue_50,
            text: blue_500,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    promo: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_150,
            text: white,
        },
        light: {
            background: background_150,
            text: white,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    cash: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_150,
            text: white,
        },
        light: {
            background: background_150,
            text: white,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    vipSilver: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_150,
            text: white,
        },
        light: {
            background: background_150,
            text: white,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    vipGold: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_150,
            text: white,
        },
        light: {
            background: background_150,
            text: white,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    home: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_150,
            text: white,
        },
        light: {
            background: background_150,
            text: white,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    family: {
        darkSolid: {
            background: blue_200,
            text: white,
        },
        solid: {
            background: blue_200,
            text: white,
        },
        light: {
            background: blue_50,
            text: blue_500,
        },
        bordered: {
            background: white,
            border: blue_300,
            text: blue_200,
        },
        plainText: {
            text: blue_200,
        },
    },
    new: {
        darkSolid: {
            background: orange_50,
            text: orange_500,
        },
        solid: {
            background: orange_50,
            text: orange_500,
        },
        light: {
            background: orange_50,
            text: orange_500,
        },
        bordered: {
            background: orange_50,
            border: orange_500,
            text: orange_500,
        },
        plainText: {
            text: orange_500,
        },
    },
    flights: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_150,
            text: white,
        },
        light: {
            background: background_150,
            text: white,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    text: {
        0: text_white,
        100: text_100,
        300: text_300,
        500: text_500,
        800: text_800,
        inherit: 'inherit',
    },
    background: {
        0: white,
        20: background_20,
        50: background_50,
        100: background_100,
        150: background_150,
        200: background_200,
        300: background_300,
        500: background_500,
        800: background_800,
        gradient: {
            start: background_50,
            end: background_150,
        },
    },
    dim: {
        pressState: alpha_black_15,
        modal: alpha_black_60,
        coachmark: alpha_black_80,
        navigationTab: alpha_white_20,
        hoverState: alpha_white_30,
    },
};

export default {
    ...coreDefaultTheme.palette,
    ...palette,
};
